import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from '@/i18n/translations';
import { ErrorState } from '@leanin/ui';
import { RetryButton } from '@/components/states/index';
import { eventsKeys } from '@/lib/queries/events';
import { useMember } from '@/lib/use-session';
import { CreateEventForm } from './-components/CreateEventForm';

type Search = { groupId?: string };

export const Route = createFileRoute('/_authed/events/new/')({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    ...(typeof raw['groupId'] === 'string' && raw['groupId'].length > 0
      ? { groupId: raw['groupId'] }
      : {}),
  }),
  component: CreateEventPage,
});

function CreateEventPage() {
  const t = useTranslations('Events');
  const search = Route.useSearch();
  const me = useMember();
  const isAdmin = me?.platformRole === 'admin';

  const circlesQuery = useQuery(eventsKeys.myLeadingCircleOptions());
  const networksQuery = useQuery(eventsKeys.myLeadingNetworkOptions());
  const platformQuery = useQuery({
    ...eventsKeys.platformCalendars(),
    enabled: isAdmin,
  });

  if (circlesQuery.isLoading || networksQuery.isLoading) return null;
  if (circlesQuery.isError || networksQuery.isError) {
    return (
      <ErrorState
        title={t('new.loadErrorTitle')}
        body={t('new.loadErrorBody')}
        primary={<RetryButton />}
      />
    );
  }

  return (
    <CreateEventForm
      circles={circlesQuery.data ?? []}
      networks={networksQuery.data ?? []}
      platformCalendars={platformQuery.data ?? []}
      isAdmin={isAdmin}
      {...(search.groupId !== undefined ? { initialGroupId: search.groupId } : {})}
    />
  );
}
